/* eslint-disable react/prop-types */
import {
  Box,
  TableCell,
  TableRow,
  Table,
  TableContainer,
  TableHead,
  Typography,
  TableBody,
} from '@mui/material';
import NoSimOutlinedIcon from '@mui/icons-material/NoSimOutlined';
import { useParams } from 'react-router';

import { ReceiptDocumentItem } from '../../types/property.types';
import {
  deleteReceiptDocument,
  deleteReceiptDocumentFromS3,
  downloadReceiptDocumentFromS3,
  getPresignedUrl,
} from '../../api/get-properties';

import styles from './received-materials-list.module.css';

import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { PaperBoxStyles } from '@/components/styles';

interface ReceivedMaterialsListProps {
  readonly files: ReceiptDocumentItem[];
  readonly handleFetchInitialReceiptDocuments: () => Promise<void>;
  readonly canUpdateProperty: boolean;
  readonly onDocumentsUploaded?: () => void;
}

const ReceivedMaterialsList: React.FC<ReceivedMaterialsListProps> = ({
  files,
  handleFetchInitialReceiptDocuments,
  canUpdateProperty,
  onDocumentsUploaded,
}) => {
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsLoading } = useLoadingProgress();
  const { propertyId } = useParams();
  const oldReceiptDocuments = files.filter((file) => !!file.id);
  const handleDeleteFile = async (id: string) => {
    if (!propertyId) {
      return;
    }
    setIsLoading(true);
    try {
      let result = await deleteReceiptDocument(propertyId, id);
      const urlDeleted = result.url;
      deleteReceiptDocumentFromS3(urlDeleted);
      setSnackbarMessage(MESSAGES.DELETE_RECEIPT_SUCCESS);
      onDocumentsUploaded?.();
    } catch (error) {
      const message = (error as Error).message;
      setConfirmDialog({
        title: message,
      });
    } finally {
      await handleFetchInitialReceiptDocuments();
      setIsLoading(false);
    }
  };
  const handleDelete = (id: string) => {
    setConfirmDialog({
      title: '受領資料を削除します。削除後は復旧できません。',
      content: '',
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
      confirmDialog: () => {
        void handleDeleteFile(id);
      },
    });
  };

  const handleDowLoadFile = async (file: ReceiptDocumentItem) => {
    const url = await getPresignedUrl(propertyId ?? '', file.title ?? '', 'download');
    await downloadReceiptDocumentFromS3(url.url, file.title ?? '');
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Typography variant='h5' fontWeight='bold' className={styles.title}>
        受領資料一覧
      </Typography>

      {oldReceiptDocuments.length == 0 ? (
        <Box className={styles.fileEmptyContainer}>
          <NoSimOutlinedIcon className={styles.fileEmptyIcon} />
          <Box className={styles.fileEmptyTextContainer}>
            <Typography variant='body1' className={styles.fileEmptyText}>
              ファイルがまだアップロードされていません。
            </Typography>
            <Typography variant='body1' className={styles.fileEmptyText}>
              ファイルを画面にドラッグするか上記ボタンよりファイルをアップロードください。
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box className={styles.fileContainer}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>資料名</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {oldReceiptDocuments.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Box
                        onClick={() => void handleDowLoadFile(file)}
                        className={styles.cellContent}
                      >
                        {file.title}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {canUpdateProperty && (
                        <Box className={styles.cellContent}>
                          <Box
                            className={styles.deleteIcon}
                            onClick={() => handleDelete(file.id ?? '')}
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </PaperBoxStyles>
  );
};

export default ReceivedMaterialsList;
